// PERMANENT: FAQ page — AEO / featured-snippet friendly. Do not remove.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const NAVY = "#1B2F5E";
const GOLD = "#C9A96E";

const FAQS = [
  { q: "What is the SummaryMaps Journal?",
    a: "The SummaryMaps Journal is a free publication by Summary Maps LLC featuring in-depth essays, visual guides, and book breakdowns on mind mapping, AI, visual learning, and productivity. New essays are published several times a week." },
  { q: "Who writes the SummaryMaps Journal?",
    a: "The journal is edited and published by Travis Moore, founder of Summary Maps. Some articles are drafted with AI assistance; we check claims before and after publishing and correct mistakes openly with a dated correction note." },
  { q: "Is the SummaryMaps Journal free?",
    a: "Yes — all articles on summarymapsjournal.com are completely free to read. No account or subscription required." },
  { q: "What topics does the journal cover?",
    a: "The journal covers mind mapping, book summaries and reviews, AI and technology trends, visual learning science, productivity, education, and personal development." },
  { q: "How often are new articles published?",
    a: "New essays are published several times a week. The newsletter is one weekly email — a digest of the newest essays, sent every Monday." },
  { q: "How is the journal connected to Summary Maps?",
    a: "The SummaryMaps Journal is the content arm of Summary Maps LLC. While the journal is free to read, it showcases how Summary Maps turns complex books and ideas into visual knowledge maps at summarymaps.com." },
  { q: "Can I listen to articles on the journal?",
    a: "Yes — most articles include a full audio read-aloud generated by an AI voice (OpenAI Nova). Look for the audio player at the top of each article." },
  { q: "How do I get the free ebook from the journal?",
    a: "After 10 seconds on any journal page, a popup offers the free ebook \"Beat Information Overload.\" Enter your email and it will be sent to you instantly." },
  { q: "How do I subscribe to the journal newsletter?",
    a: "Enter your email in the popup or footer subscribe form. You'll receive one weekly digest email every Monday with the newest essays. No spam — unsubscribe anytime." },
  { q: "Are the book reviews on the journal AI-generated?",
    a: "Articles are AI-assisted and editorially reviewed by Travis Moore. The journal uses AI to research, draft, and structure content, which is then checked for quality and accuracy before publishing — and corrected openly when we get something wrong." },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a },
  })),
};

function FAQItem({ q, a, idx, open, onToggle }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: "#ffffff", border: `1px solid ${NAVY}18` }}
      data-testid={`faq-item-${idx}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`faq-answer-${idx}`}
        className="w-full flex items-start gap-3 text-left px-5 sm:px-6 py-4 sm:py-5"
        style={{ background: "transparent", border: "none", cursor: "pointer" }}
        data-testid={`faq-question-${idx}`}
      >
        <span
          className="font-serif text-base sm:text-lg font-semibold flex-1"
          style={{ color: NAVY, lineHeight: 1.35 }}
        >
          {q}
        </span>
        <ChevronDown
          className="shrink-0 mt-0.5 transition-transform"
          size={18}
          style={{ color: GOLD, transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      {open ? (
        <div
          id={`faq-answer-${idx}`}
          className="px-5 sm:px-6 pb-5 sm:pb-6 pt-0"
          style={{ color: "#4A5568", fontSize: "0.98rem", lineHeight: 1.7 }}
          data-testid={`faq-answer-${idx}`}
        >
          {a}
        </div>
      ) : null}
    </div>
  );
}

export default function FAQPage() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="min-h-screen topo-bg" data-testid="faq-page">
      <SeoHead
        title="FAQ"
        description="Frequently asked questions about the SummaryMaps Journal — topics, publishing cadence, audio articles, newsletter, and how it connects to Summary Maps."
        schemas={[FAQ_SCHEMA]}
      />
      <main
        className="relative z-10 mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-20 sm:pb-24"
        style={{ maxWidth: 780 }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to the journal
        </Link>

        <div className="text-center max-w-2xl mx-auto">
          <div
            className="text-xs uppercase tracking-[0.22em] font-semibold mb-3"
            style={{ color: GOLD }}
          >
            Frequently Asked
          </div>
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight"
            style={{ color: NAVY }}
          >
            Questions & Answers
          </h1>
          <p className="mt-4 sm:mt-5 text-base sm:text-lg leading-relaxed" style={{ color: "#4A5568" }}>
            Everything you might want to know about the SummaryMaps Journal — how we write, how often we publish, and how to get the most out of what we make.
          </p>
        </div>

        <div className="mt-12 sm:mt-16 space-y-3 sm:space-y-4" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <FAQItem
              key={i}
              idx={i}
              q={f.q}
              a={f.a}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </main>
    </div>
  );
}
